import * as module from "module";
import * as JavascriptModuleResolver from "OK/JavascriptModuleResolver";
import * as RequireJSModuleResolver from "OK/RequireJSModuleResolver";
import { IReactAppBlock } from '@ok/interfaces/core/react';

const RUNTIME_MODULES = [
    'OK/pts',
    'OK/pms',
    'OK/webapi',
    'OK/css-loader',
    'OK/LazyIconsCache',
    'OK/ClientPropertiesEventBuses',
];

export type AppModule = {
    tags: Record<string, IReactAppBlock>,
};

export type AssetsResources = {
    jsBundle: string[],
    startupJsBundles: string[],
    cssBundle: string[],
    l10nPackages: string[],
    cfgPackages: string[],
    requireJSModules: string[],
}

type LoadingResourceFormattedError = {
    message: string;
    requireType: string;
    requireModules: string[];
    stack: string;
};

class LoadingResourceError extends Error {
    stack: string;
    requireType?: string;
    requireModules?: string[];

    constructor(message: string, stack: string, requireType?: string, requireModules?: string[]) {
        super(message);
        this.stack = stack;
        this.requireType = requireType;
        this.requireModules = requireModules;
    }
}

export class LoadingLibrariesError extends LoadingResourceError {
}

export class LoadingDependenciesError extends LoadingResourceError {
}

/**
 * Загружает в определённом порядке все ресурсы, необходимые для запуска Реакт-приложения.
 */
export default class ReactResourceResolver {
    public static resolveModule(requiredResources: AssetsResources): Promise<AppModule> {
        const maxAttemptsRetryCount = module.config().retryRequireLoad || 3;

        // Запрашиваем базовые библиотеки Реакта (вендорный бандл + core-модули).
        const startupReactModules = Promise.all(requiredResources.startupJsBundles.map(moduleName => {
            return JavascriptModuleResolver.load(getFullPath(moduleName), {
                maxAttemptsCount: maxAttemptsRetryCount,
            });
        })).catch(error => {
            const { message, stack } = error;
            throw new LoadingLibrariesError(message, stack);
        });

        // Запрашиваем необходимые зависимости из старого стека.
        const initialRequireJsModules = RequireJSModuleResolver.loadWithRetry([
            // Общие зависимости для всех Реакт-приложений.
            ...RUNTIME_MODULES,
            // Зависимости конкретного Реакт-блока.
            ...(requiredResources.requireJSModules || [])
        ], {
            maxAttemptsCount: maxAttemptsRetryCount,
        })
            .catch(error => {
                const { message, stack, requireType, requireModules } = formatRequireError(error);
                throw new LoadingLibrariesError(message, stack, requireType, requireModules);
            });

        return Promise.all([startupReactModules, initialRequireJsModules])
            .then(() => {
                const dependencies = [];

                // Запрашиваем бандл с реакт-приложением.
                requiredResources.jsBundle.forEach(bundle => {
                    dependencies.push('OK/RequireJSModuleResolver!' + getFullPath(bundle));
                })


                // Запрашиваем `css`, если он есть.
                if (requiredResources.cssBundle) {
                    requiredResources.cssBundle.forEach(bundle => {
                        dependencies.push('OK/css-loader!' + getFullPath(bundle));
                    });
                }

                // Запрашиваем пакеты с локализацией.
                if (requiredResources.l10nPackages.length > 0) {
                    dependencies.push('OK/pts!' + requiredResources.l10nPackages.join(","));
                }

                // Запрашиваем модули с клиентскими конфигурациями.
                requiredResources.cfgPackages.forEach(cfgPackage => {
                    dependencies.push('OK/pms!' + cfgPackage);
                });

                return RequireJSModuleResolver.loadWithRetry(dependencies, {
                    maxAttemptsCount: maxAttemptsRetryCount,
                })
                .catch(error => {
                    const { message, stack, requireType, requireModules } = formatRequireError(error);
                    throw new LoadingDependenciesError(message, stack, requireType, requireModules);
                })
            }) as Promise<AppModule>;
    }
}

function formatRequireError(e: RequireJSModuleResolver.LoadingError): LoadingResourceFormattedError {
    const originalError = e.originalError || e;
    return {
        message: originalError.message || e.message || '',
        requireType: e.requireType,
        requireModules: e.requireModules!,
        stack: originalError.stack || e.stack || ''
    }
}

function getFullPath(name: string): string {
    const baseUrl = module.config().baseUrl || '/res/react';

    return baseUrl + (baseUrl.endsWith('/') ? '' : '/') + name;
}
